import {
  Card,
  createStyles,
  makeStyles,
  useDialogs,
  useIsMobile,
  ActionButton,
  BulkActionBar,
  BulkActionButton,
  ConfirmationDialog,
  Hidden,
  ResultsTable,
  TableCell,
  TableRow,
  useClientOptions,
  useUser,
  useUserPermissions,
  Link,
  Checkbox
} from '@paintscout/ui'
import type {
  TableCellProps,
  MuiTheme as Theme,
  StyleClasses,
  Auth0UserWithClaims,
  SelectableColumn
} from '@paintscout/ui'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import type { Contact } from '@paintscout/api'
import { isUserAssigned, isUserOwner } from '@paintscout/util/builder'
import * as Sentry from '@sentry/react'
import { useFlags } from 'shared/components/common/FlagsProvider'
import MobilePersonRow from 'src/components/common/MobilePersonRow'
import find from 'lodash/find'
import { track } from 'shared/util/analytics'
import moment from 'moment'
import { useSnackbar } from 'notistack'
import React, { useCallback, useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useContactMutations } from 'shared/hooks'

export interface ContactTableProps {
  classes?: StyleClasses<typeof useStyles>
  contacts: Array<Partial<Contact>>
  loading?: boolean
  totalRows: number
  rowsPerPage: number

  checkboxes?: boolean
  actions?: {
    edit?: boolean
    delete?: boolean
  }

  sortKey?: string
  sort?: string

  page: number
  pages: number
  disablePagination?: boolean
  skipRefetchQueries?: boolean

  onFetchNextPage?: (page: number) => any
  onPageChange?: (page: number) => any
  onRequestSort?: (ev: React.MouseEvent, name: string, direction: 'asc' | 'desc') => void
  onDelete?: (ids: string[]) => any
}

export interface ContactRow {
  _id: string
  _rev: string
  name: string
  company?: string
  email?: string
  owner: string
  assignedUsers?: string[]
  isAssigned: boolean
  isOwner: boolean
  updated: string
  created: string
  address?: string
  city?: string
  province?: string
  postal?: string
  phone_number?: string
  phone_ext?: string
}

export type ContactResult = Partial<Contact>

const useStyles = makeStyles<Theme>((theme: Theme) =>
  createStyles({
    root: {
      overflow: 'hidden'
    },
    tableRow: {
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand.Neutral.P98
      }
    },
    emailCell: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: 200,
      [theme.breakpoints.up('lg')]: {
        maxWidth: 300
      }
    }
  })
)
/**
 * Displays contacts in a ResultsTable
 */
function ContactTable({
  skipRefetchQueries,
  disablePagination,
  onFetchNextPage,
  onRequestSort,
  actions = {},
  onPageChange,
  rowsPerPage,
  checkboxes,
  contacts,
  onDelete,
  loading,
  sort,
  ...otherProps
}: ContactTableProps) {
  const { permissionToEditAllContacts, permissionToEditAssignedContacts } = useUserPermissions()
  const { openDialog, dismissDialog } = useDialogs()
  const { deleteContacts } = useContactMutations()
  const { enqueueSnackbar } = useSnackbar()
  const { crm: crmFlag } = useFlags()
  const { user, trialExpired } = useUser()
  const { options } = useClientOptions()
  const isMobile = useIsMobile()
  const isMd = useIsMobile({ custom: 1214 })
  const history = useHistory()
  const classes = useStyles()

  const dateFormat = options.options.dateFormat.momentValue
  const [selectedRows, setSelectedRows] = useState([])
  const rows = contacts.map((contact: ContactResult) => mapContactToRow(contact, user)).filter(Boolean)
  const columns: SelectableColumn[] = [
    { name: 'Name', field: 'first_name', sortable: true },
    { name: 'Company', field: 'company', sortable: true },
    { name: 'Email', field: 'email', sortable: true },
    { name: 'Owner', field: 'owner.firstName', sortable: true },
    !isMd && { name: 'Updated', field: 'updated', align: 'right', sortable: true },
    { name: 'Created', field: 'created', align: 'right', sortable: true }
  ]

  useEffect(() => {
    setSelectedRows([])
  }, [contacts])

  const handlePageChange = useCallback(
    (page: number) => {
      const nextPageFirstIndex = (page - 1) * rowsPerPage
      const dataExists = !!contacts[nextPageFirstIndex]

      if (onPageChange) {
        onPageChange(page)
      }

      if (!dataExists && onFetchNextPage) {
        onFetchNextPage(page)
      }
    },
    [onPageChange, onFetchNextPage, contacts, rowsPerPage]
  )

  const handleRowClick = useCallback(
    (ev: React.MouseEvent<HTMLElement>, contact: ContactRow) => {
      const fullContact = find(contacts, { _id: contact._id })
      history.push(`/people/contact/${encodeURIComponent(fullContact._id)}`)
    },
    [contacts]
  )

  const handleAction = useCallback(
    (action: string, contactRows: ContactRow[]) => {
      const ids = contactRows.map((contact) => contact._id)
      const names = contactRows.map((contact) => contact.name?.trim()).filter((name): name is string => !!name)

      if (action === 'edit') {
        return history.push(`/people/contact/${encodeURIComponent(contactRows[0]._id)}`)
      } else {
        openDialog(ConfirmationDialog, {
          message: (
            <span>
              This will permanently delete{' '}
              {contactRows.length === 1
                ? `${names[0] || 'this contact'}${crmFlag ? ' and cancel all their upcoming events' : ''}.`
                : `all selected contacts${crmFlag ? ' and cancel all their upcoming events' : ''}.`}
            </span>
          ),
          onConfirm: async () => {
            switch (action) {
              case 'delete':
                track('contacts_bulk_deleted', { contactIds: ids, count: ids.length })
                deleteContacts(ids, skipRefetchQueries).catch((error) => {
                  Sentry.captureException(error)
                  enqueueSnackbar('Unable to delete Contact', { variant: 'error' })
                })
                if (onDelete) {
                  onDelete(ids)
                }
                break
            }
            setSelectedRows([])
            dismissDialog()
          },
          onCancel: dismissDialog
        })
      }
    },
    [handleRowClick, onDelete]
  )

  const rowActions = useCallback(
    (row: ContactRow) => {
      const allowedActions = []

      if (
        actions.edit &&
        (row.isOwner || permissionToEditAllContacts || (row.isAssigned && permissionToEditAssignedContacts))
      ) {
        allowedActions.push({
          key: 'edit',
          label: 'Edit',
          icon: EditIcon
        })
      }

      if (
        actions.delete &&
        (row.isOwner || permissionToEditAllContacts || (row.isAssigned && permissionToEditAssignedContacts))
      ) {
        allowedActions.push({
          key: 'delete',
          label: 'Delete',
          icon: DeleteIcon
        })
      }
      return allowedActions
    },
    [actions]
  )

  const createSortHandler = (property: string) => (event: React.MouseEvent) => {
    if (onRequestSort) {
      // get current sort direction
      const currentSort = getSortDirection(property) || 'asc'

      // flip the sort direction
      const newSort = currentSort === 'asc' ? 'desc' : 'asc'

      onRequestSort(event, property, newSort)
    }
  }

  const getSortDirection = (property: string) => {
    const currentSortDirection = sort && sort.startsWith('-') ? 'desc' : 'asc'
    const currentSortName = sort && sort.startsWith('-') ? sort.slice(1) : sort

    if (currentSortName === property) {
      return currentSortDirection
    }
  }

  const renderRow = useCallback(
    (row: ContactRow) => {
      if (isMobile) {
        const addressLine = [row.address, row.city, row.province, row.postal].filter(Boolean).join(', ')
        const phoneLine = [row.phone_number, row.phone_ext ? `ext. ${row.phone_ext}` : null].filter(Boolean).join(' ')
        const actions = rowActions(row)

        return (
          <MobilePersonRow
            key={row._id}
            data-testid={`contact-${row._id}`}
            className={classes.tableRow}
            name={row.name}
            addressLine={addressLine}
            phoneLine={phoneLine}
            actions={actions}
            actionsDisabled={trialExpired}
            onClick={(ev) => handleRowClick(ev, row)}
            onActionClick={(action) => handleAction(action, [row])}
          />
        )
      }

      return (
        <TableRow key={row._id} data-testid={`contact-${row._id}`} className={classes.tableRow}>
          {checkboxes && (
            <TableCell padding="none" style={{ width: 68, minWidth: 68, maxWidth: 68 }}>
              <Checkbox
                checked={!!selectedRows.find((r) => r._id === row._id)}
                onChange={(ev, checked) => {
                  setSelectedRows(checked ? [...selectedRows, row] : selectedRows.filter((r) => r._id !== row._id))
                }}
              />
            </TableCell>
          )}
          <TableCellLink row={row}>{row.name}</TableCellLink>

          <Hidden xsDown>
            <TableCellLink row={row}>{row.company || ''}</TableCellLink>
          </Hidden>

          <Hidden smDown>
            <TableCellLink row={row} className={classes.emailCell}>
              {row?.email || ''}
            </TableCellLink>
          </Hidden>

          <Hidden smDown>
            <TableCellLink row={row}>{row.owner}</TableCellLink>
          </Hidden>

          <Hidden mdDown>
            <TableCellLink row={row} align="right">
              {row.updated ? moment(parseInt(row.updated, 10)).format(dateFormat) : null}
            </TableCellLink>
          </Hidden>

          <TableCellLink row={row} align="right">
            {row.created ? moment(parseInt(row.created, 10)).format(dateFormat) : null}
          </TableCellLink>

          {rowActions.length > 0 && (
            <TableCell align="right" style={{ width: 68, minWidth: 68, maxWidth: 68 }}>
              <ActionButton
                data-testid="actions"
                actions={rowActions(row)}
                disabled={rowActions(row).length === 0 || trialExpired}
                onActionClick={(ev, action) => handleAction(action, [row])}
              />
            </TableCell>
          )}
        </TableRow>
      )
    },
    [rowActions, selectedRows, isMobile, checkboxes]
  )

  return (
    <>
      <Card data-testid={'contact-list'}>
        <ResultsTable
          classes={{ root: classes.root }}
          noResultsMessage="No contacts found."
          columns={
            <TableRow>
              {!isMobile && (
                <>
                  {checkboxes && <TableCell isControl padding="none" />}
                  {columns.filter(Boolean).map((column) => {
                    const sortable = !!sort && !!column.sortable
                    return (
                      <TableCell
                        key={column.field}
                        align={column.align || 'left'}
                        fadeWhenInactive
                        sortable={sortable}
                        sortDirection={getSortDirection(column.field)}
                        onClick={sortable ? createSortHandler(column.field) : null}
                        style={{ minWidth: column.minWidth || undefined }}
                      >
                        {column.name}
                      </TableCell>
                    )
                  })}
                  {rowActions.length > 0 && <TableCell />}
                </>
              )}
            </TableRow>
          }
          renderRow={renderRow}
          rows={rows}
          rowsPerPage={rowsPerPage}
          disablePagination={disablePagination}
          loading={loading}
          {...otherProps}
          // otherProps wrappers
          onPageChange={handlePageChange}
        />
      </Card>
      <BulkActionBar
        itemName={'Contact'}
        pluralItemName={'Contacts'}
        selectedItems={selectedRows}
        onDeselectAll={() => setSelectedRows([])}
      >
        {actions.delete && (
          <BulkActionButton disabled={loading} icon={DeleteIcon} onClick={() => handleAction('delete', selectedRows)}>
            Delete
          </BulkActionButton>
        )}
      </BulkActionBar>
    </>
  )
}

function mapContactToRow(contact: Partial<Contact>, user: Auth0UserWithClaims): ContactRow {
  if (!contact) {
    return null
  }
  const owner = contact.owner ? [contact.owner?.firstName, contact.owner?.lastName].filter(Boolean).join(' ') : ''
  const isAssigned = isUserAssigned(contact, user.user_id)
  const isOwner = isUserOwner(contact, user.user_id)

  return {
    _id: contact._id,
    _rev: contact._rev,
    assignedUsers: contact.assignedUsers,
    name: `${contact.first_name} ${contact.last_name}`,
    company: contact.company,
    email: contact.email,
    isAssigned,
    isOwner,
    owner,
    updated: contact.updated_date?.toString() ?? '',
    created: contact.created_date?.toString() ?? '',
    address: contact.address ?? '',
    city: contact.city ?? '',
    province: contact.province ?? '',
    postal: contact.postal ?? '',
    phone_number: contact.phone_number ?? '',
    phone_ext: contact.phone_ext ?? ''
  }
}

/**
 * Renders a TableCell with a <Link> component to the contact
 */
function TableCellLink({ row, ...other }: { row: ContactRow } & TableCellProps) {
  return (
    <TableCell
      component={(cProps: any) => (
        <Link to={{ pathname: `/people/contact/${encodeURIComponent(row._id)}` }} {...cProps} />
      )}
      {...other}
    />
  )
}

export default ContactTable
